import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import swal from 'sweetalert';
import { getGuideAdmin, createGuide, updateGuide, generateContentDraft } from '../../../api/contentAdminApi';
import { GUIDE_CATEGORIES } from '../../../data/cmsAdminConfig';
import {
	getGuideCategoryLabel,
	getGuidePublicPath,
	getGuideVisibility,
	GUIDES_INDEX_PATH,
} from '../../../data/guideCategoryMeta';
import {
	getPublicSiteUrl,
	isCmsPublished,
	prepareCmsPayload,
	slugifyTitle,
	validateGuideForPublish,
	normalizeCmsFormData,
} from '../../../utils/cmsPublishHelpers';
import { CmsEditorialFields, CmsFormShell } from './CmsFormFields';

const EMPTY = {
	title: '', slug: '', category: 'visa', summary: '', body: '',
	official_source_name: '', official_source_url: '', subject_to_change: false,
	editorial_status: 'draft', published: false,
};

async function afterGuideSave({ payload, savedSlug, history }) {
	const articlePath = getGuidePublicPath(savedSlug);
	const articleUrl = getPublicSiteUrl(articlePath);
	const visibility = getGuideVisibility(payload.category);
	const hubNote = visibility.hub ? ` and ${visibility.hub}` : '';

	if (isCmsPublished(payload)) {
		const action = await swal({
			title: 'Published on visitallnepal.com',
			text: `Tourists can read it now.\n\nArticle: ${articlePath}\nAlso listed on ${GUIDES_INDEX_PATH}${hubNote}.`,
			icon: 'success',
			buttons: {
				view: { text: 'View on site', value: 'view', className: 'swal-button--confirm' },
				guides: { text: 'All guides page', value: 'guides' },
				list: { text: 'Back to admin list', value: 'list' },
			},
		});
		if (action === 'view') window.open(articleUrl, '_blank');
		else if (action === 'guides') window.open(getPublicSiteUrl(GUIDES_INDEX_PATH), '_blank');
		history.push('/admin/cms/guides');
		return;
	}

	await swal(
		'Saved as draft',
		'Not visible to tourists yet. Set Editorial status to Published when ready, then save again.',
		'success',
	);
	history.push('/admin/cms/guides');
}

export default function GuideAdminForm() {
	const { slug } = useParams();
	const history = useHistory();
	const isEdit = Boolean(slug);
	const [form, setForm] = useState(EMPTY);
	const [saving, setSaving] = useState(false);
	const [generating, setGenerating] = useState(false);
	const [slugTouched, setSlugTouched] = useState(false);

	useEffect(() => {
		if (!isEdit) return;
		getGuideAdmin(slug)
			.then((res) => {
				setForm(normalizeCmsFormData(res.data?.guide || res.data, EMPTY));
				setSlugTouched(true);
			})
			.catch(() => swal('Error', 'Could not load guide', 'error'));
	}, [isEdit, slug]);

	const onChange = (key, value) => setForm((f) => ({ ...f, [key]: value }));

	const onTitleChange = (title) => {
		setForm((f) => ({
			...f,
			title,
			...(!isEdit && !slugTouched ? { slug: slugifyTitle(title) } : {}),
		}));
	};

	const handleGenerate = async () => {
		if (!form.title?.trim()) {
			swal('Title required', 'Enter a guide title first, then generate.', 'warning');
			return;
		}
		setGenerating(true);
		try {
			const res = await generateContentDraft({
				kind: 'guide',
				title: form.title.trim(),
				category: form.category,
				context: form.summary || undefined,
			});
			const draft = res.data?.draft || {};
			setForm((f) => ({
				...f,
				summary: draft.summary || f.summary,
				body: draft.body || f.body,
				meta_title: draft.meta_title || f.meta_title,
				meta_description: draft.meta_description || f.meta_description,
				subject_to_change: draft.subject_to_change ?? f.subject_to_change,
				official_source_name: draft.official_source_name || f.official_source_name,
				official_source_url: draft.official_source_url || f.official_source_url,
				editorial_status: 'draft',
				published: false,
			}));
			swal('Draft generated', 'Review and edit before publishing.', 'success');
		} catch (err) {
			swal('Generation failed', err.response?.data?.errors?.[0] || 'Could not generate draft', 'error');
		} finally {
			setGenerating(false);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const payload = prepareCmsPayload(form);

		if (isCmsPublished(payload)) {
			const missing = validateGuideForPublish(payload);
			if (missing.length > 0) {
				swal('Cannot publish yet', `Add: ${missing.join(', ')}`, 'warning');
				return;
			}
		}

		setSaving(true);
		try {
			let savedSlug = payload.slug;
			if (isEdit) {
				await updateGuide(slug, payload);
			} else {
				const res = await createGuide(payload);
				const saved = res.data?.guide || res.data;
				savedSlug = saved?.slug || payload.slug;
			}
			await afterGuideSave({ payload, savedSlug, history });
		} catch (err) {
			swal('Error', err.response?.data?.errors?.[0] || 'Could not save', 'error');
		} finally {
			setSaving(false);
		}
	};

	const visibility = form.category ? getGuideVisibility(form.category) : null;
	const previewSlug = form.slug || slugifyTitle(form.title);
	const liveNow = isCmsPublished(form);

	return (
		<CmsFormShell
			title={isEdit ? `Edit: ${form.title}` : 'New knowledge guide'}
			backHref="/admin/cms/guides"
			onSubmit={handleSubmit}
			saving={saving}
			extraActions={(
				<button type="button" className="btn btn-outline-primary mr-2" onClick={handleGenerate} disabled={generating || saving}>
					{generating ? 'Generating…' : 'Generate draft with AI'}
				</button>
			)}
		>
			{visibility && previewSlug && (
				<div className={`admin-cms-visibility${liveNow ? ' admin-cms-visibility--live' : ''}`}>
					<h3>{liveNow ? 'Live on the public site' : 'Where this will appear after you publish'}</h3>
					<ul>
						<li>
							<strong>All guides:</strong>{' '}
							<a href={getPublicSiteUrl(GUIDES_INDEX_PATH)} target="_blank" rel="noopener noreferrer">{GUIDES_INDEX_PATH}</a>
						</li>
						{visibility.hub && (
							<li>
								<strong>{getGuideCategoryLabel(form.category)} hub:</strong>{' '}
								<a href={getPublicSiteUrl(visibility.hub)} target="_blank" rel="noopener noreferrer">{visibility.hub}</a>
							</li>
						)}
						<li>
							<strong>Article URL:</strong>{' '}
							{liveNow ? (
								<a href={getPublicSiteUrl(getGuidePublicPath(previewSlug))} target="_blank" rel="noopener noreferrer">
									{getGuidePublicPath(previewSlug)}
								</a>
							) : (
								<span>{getGuidePublicPath(previewSlug)}</span>
							)}
						</li>
					</ul>
					{liveNow && (
						<p className="mb-0 mt-2">
							<a href={getPublicSiteUrl(getGuidePublicPath(previewSlug))} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-success">
								Open live page →
							</a>
						</p>
					)}
				</div>
			)}
			<fieldset className="admin-cms-form__section">
				<legend>Basics</legend>
				<div className="admin-cms-form__row">
					<label><span>Title</span><input required value={form.title} onChange={(e) => onTitleChange(e.target.value)} /></label>
					<label>
						<span>Slug (URL)</span>
						<input
							required
							value={form.slug}
							onChange={(e) => { setSlugTouched(true); onChange('slug', e.target.value); }}
							disabled={isEdit}
						/>
					</label>
				</div>
				<label>
					<span>Category — controls which hub page lists this guide</span>
					<select value={form.category} onChange={(e) => onChange('category', e.target.value)}>
						{GUIDE_CATEGORIES.map((c) => (
							<option key={c} value={c}>{getGuideCategoryLabel(c)}</option>
						))}
					</select>
				</label>
				<label><span>Summary</span><textarea rows={2} value={form.summary || ''} onChange={(e) => onChange('summary', e.target.value)} /></label>
				<label><span>Body</span><textarea rows={10} value={form.body || ''} onChange={(e) => onChange('body', e.target.value)} /></label>
				<div className="admin-cms-form__row">
					<label><span>Official source name</span><input value={form.official_source_name || ''} onChange={(e) => onChange('official_source_name', e.target.value)} /></label>
					<label><span>Official source URL</span><input value={form.official_source_url || ''} onChange={(e) => onChange('official_source_url', e.target.value)} /></label>
				</div>
				<label className="admin-cms-form__check">
					<input type="checkbox" checked={!!form.subject_to_change} onChange={(e) => onChange('subject_to_change', e.target.checked)} />
					Subject to change (fees, rules, etc.)
				</label>
			</fieldset>
			<CmsEditorialFields form={form} onChange={onChange} />
		</CmsFormShell>
	);
}
